import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { features } from '../helpers/tenant.features';
import { FacilityTypeChoices } from '../models/constants';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagsService {
  private featureFlags: any = null;

  constructor(private http: HttpClient) {}

  async getFeatureFlags(): Promise<void> {
    try {
      const result = await this.getFeatureFlags$().toPromise();
      this.featureFlags = result.data;
    } catch (error) {
      this.featureFlags = null;
    }
    features.lotTracking = this.getByEnv('process', 'lot_tracking') === true;
    features.budgetsAndSavings = this.getByEnv('process', 'budgets_and_savings') === true;
    features.wishlistSoftBlock = this.getByEnv('ui', 'wishlist_soft_block') === true;
    features.emailSubscriptions = this.getByEnv('ui', 'email_subscriptions') === true;
    features.multiSellerCatalog = this.getByEnv('process', 'multi_seller_catalog') === true;
    features.accountBucketPricing = this.getByEnv('ui', 'account_bucket_pricing') === true;
    features.termsGatedPricing = this.getByEnv('process', 'terms_gated_pricing') === true;
    features.registrationDscsaRequired = this.getByEnv('process', 'registration_dscsa_required') === true;
    features.marketplaceCheckout = this.getByEnv('process', 'marketplace_checkout') === true;
    features.marketplaceTerms = this.getByEnv('process', 'marketplace_terms') === true;
    features.marketplaceListingEditor = this.getByEnv('ui', 'marketplace_listing_editor') === true;
    features.marketplaceNavigation = this.getByEnv('ui', 'marketplace_navigation') === true;
    features.bidOrders = this.getByEnv('process', 'bid_orders') === true;
    features.rfpOrders = this.getByEnv('process', 'rfp_orders') === true;
    features.expeditedShipping = this.getByEnv('ui', 'expedited_shipping') === true;
  }

  public getFeatureFlags$(): Observable<any> {
    return this.http.get<any>(
      `${environment.api_url_v1}/services/user/ms_client/feature_flags`,
    );
  }

  get(key: string): any {
    return this.featureFlags ? this.featureFlags[key] : null;
  }

  getOrDefault(key: string): any {
    if (key in this.featureFlags) return this.featureFlags[key];
    if ('default' in this.featureFlags) return this.featureFlags['default'];
    return null;
  }

  // The namespace whose payload block applies: the pod's K8S_NAMESPACE as reported by the
  // flags endpoint (`_hydra.namespace`), so frontend and backend always resolve the same
  // block; the build-time clientNameSpace is only the fallback before/without that meta.
  get namespace(): string {
    const meta = this.featureFlags?.['_hydra'];
    return typeof meta?.namespace === 'string' && meta.namespace
      ? meta.namespace
      : environment.supplierConfig.clientNameSpace;
  }

  getByEnv(key: string, subkey: string): any {
    const featureConfig = this.get(key);
    if (!featureConfig) {
      return null;
    }
    const namespace = this.namespace;
    const defaultConfig = featureConfig['default'] || {};
    const nsConfig = featureConfig[namespace] || {};
    const config = { ...defaultConfig, ...nsConfig };
    return config[subkey];
  }

  getAll(): any {
    return this.featureFlags;
  }

  // Tenant list from `ui.facility_types`; DSCSA tenants and tenants without one get the platform list.
  facilityTypeChoices(): string[] {
    const tenantTypes = this.getByEnv('ui', 'facility_types');
    const hasTenantTypes = Array.isArray(tenantTypes) && tenantTypes.length > 0;
    return !features.registrationDscsaRequired && hasTenantTypes
      ? tenantTypes
      : FacilityTypeChoices;
  }
}
